import { Component, effect, EventEmitter, HostListener, input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  addLayersToMap,
  GeoCoords,
  HazardZoneEditEngine,
  MapboxSpecificService,
  MapControlPanelAction,
  MapControlPanelRequestBody,
  MapEditEngine,
  MapEditEngineInstancesCluster,
  metersToPretty,
  ObeliskComment,
  Pathway,
  QGraph,
  QGraphVertex,
  QGraphVertexWithCircle,
  QGraphVertexWithRadius,
  TimeSpan,
  QuestStepObjectiveType,
  drawStaticMarker,
  zoneToFeaturePolygon, adjustMapBBox
} from 'quest-atlas-angular-components';
import { MapEditorExternalApi } from '../map-editor/map-editor.interfaces';
import * as mapboxgl from 'mapbox-gl';
// @ts-ignore
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { BehaviorSubject, Subject, take, takeUntil } from 'rxjs';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { MapConfigData } from '../map-editor/map-editor.component';
import { MapEditorBase } from '../../../classes/map-editor-base';
import { TranslocoService } from '@ngneat/transloco';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface MapConfigDataV2 {
  markerReach?: QGraphVertex;
  markerFind?: QGraphVertexWithCircle;
  discovery?: QGraph;
  discoveryPercent?: number;
  startZone?: QGraph | QGraphVertexWithRadius;
  hazardZones?: QGraph[];
  startZoneHidden?: boolean;
  timer?: TimeSpan;
  overallMap?: boolean;
  pathways?: Pathway[];
  comments?: ObeliskComment[];
  allMarkersReach?: {step: number, vertex: QGraphVertex}[];
  allMarkersFind?: {step: number, vertex: QGraphVertexWithCircle}[];
  allDiscoveries?: {step: number, graph: QGraph}[];
}

@Component({
  selector: 'app-overall-map-editor',
  templateUrl: './overall-map-editor.component.html',
  styleUrl: './overall-map-editor.component.scss',
  standalone: false
})
export class OverallMapEditorComponent extends MapEditorBase implements OnInit, MapEditorExternalApi, OnDestroy {
  readonly QuestStepObjectiveType = QuestStepObjectiveType;

  editMode: 'reachMarker' | 'findMarker' | 'discover' | 'startZone' | 'hazardZone' | 'activeZone' | 'markerPath' | null = null;
  currentEditEngine?: MapEditEngine;


  title = input<string>();
  config = input<MapConfigDataV2>({});
  userGeoInput = input<GeoCoords>();

  @Output() close = new EventEmitter<void>();
  @Output() applyClick = new EventEmitter<MapConfigData>();

  mapControlPanelActions$ = new Subject<MapControlPanelAction>();
  mapControlVisibility$ = new BehaviorSubject<MapControlPanelRequestBody>({ state: 'hide' });

  hazardZonesCluster: MapEditEngineInstancesCluster<HazardZoneEditEngine> = { instances: [] };

  pathwaysFA: FormArray = this.fb.array([]);
  commentsFA: FormArray = this.fb.array([]);

  @HostListener('document:keyup', ['$event'])
  keyup(event: any) {
    event.stopPropagation();
  }

  destroy$ = new Subject<void>();

  constructor(mapboxSpecificService: MapboxSpecificService, snackBar: MatSnackBar, transloco: TranslocoService, fb: FormBuilder) {
    super(mapboxSpecificService, snackBar, transloco, fb);
    effect(() => {
      this.userGeo = this.userGeoInput();
    });
  }
  ngOnInit(): void {
    // @ts-ignore
    mapboxgl.config.ACCESS_TOKEN = environment.mapboxPublicToke;
    let centerAndZoom = {
      zoom: 2,
      center: [0, 0]
    };

    // if (!this.markingsMode()) {
      // if (this.config?.markerFind) {
      //   centerAndZoom = {
      //     zoom: 14,
      //     center: [this.config?.markerFind.coords.lng, this.config?.markerFind.coords.lat]
      //   };
      // } else if (this.config?.markerReach) {
      //   centerAndZoom = {
      //     zoom: 14,
      //     center: [this.config?.markerReach.coords.lng, this.config?.markerReach.coords.lat]
      //   };
      // } else if (this.config?.discovery) {
      //   const coords = this.config.discovery.connections.map((value) => this.config.discovery.vertices[value.startVertexId].coords);
      //   coords.push(coords[0]);
      //   const center = getCenterOfPolygon(coords);
      //
      //   centerAndZoom = {
      //     zoom: 14,
      //     center: [center.lng, center.lat]
      //   };
      // } else if (lastLocation) {
      //   centerAndZoom = {
      //     zoom: 14,
      //     center: [lastLocation.lng, lastLocation.lat]
      //   };
      // } else if (this.userGeo) {

      // }
    // } else {
    // }

    // @ts-ignore
    this.map = new mapboxgl.Map({
      container: 'map-container',
      style: 'mapbox://styles/mapbox/outdoors-v12',
      ...centerAndZoom
    });

    this.config()?.hazardZones?.forEach((value) => {
      this.addNewHazardZone(value);
    });

    // Add map controls
    this.map.addControl(
      new MapboxGeocoder({
        accessToken: environment.mapboxPublicToke,
        mapboxgl: mapboxgl
      })
    );

    this.map.addControl(new mapboxgl.NavigationControl());

    this.map.setLanguage([navigator.language, 'en']);
    this.map.on('load', () => {
      this.map.addSource('mapbox-dem', {
        'type': 'raster-dem',
        'url': 'mapbox://mapbox.mapbox-terrain-dem-v1',
        'tileSize': 512,
        'maxzoom': 14
      });

      this.map.setTerrain({ 'source': 'mapbox-dem', 'exaggeration': 1.5 });

      this.mapLoaded = true;

      addLayersToMap(this.map);

      setTimeout(() => {
        const allCoords = [];

        for (const markerReachData of this.config().allMarkersReach || []) {
          allCoords.push(markerReachData.vertex.coords);
          const marker = drawStaticMarker(markerReachData.vertex.coords,'with-text', {text: (markerReachData.step + 1).toString()});
          marker.addTo(this.map);
        }
        for (const markerFindData of this.config().allMarkersFind || []) {
          allCoords.push(markerFindData.vertex.coords);
          const marker = drawStaticMarker(markerFindData.vertex.coords,'with-text', {text: (markerFindData.step + 1).toString()});
          marker.addTo(this.map);
          // TODO this will draw only one circle, and that's it. Now it doesn't seem to draw anything anyway. Fix it a bit later. Basically need to do the same as with discovery zones a little down below
          this.drawCircle(markerFindData.vertex)
        }
        for (const discoveryZoneData of this.config().allDiscoveries || []) {
          const coords = discoveryZoneData.graph.connections.map((value) => discoveryZoneData.graph.vertices[value.startVertexId].coords);
          coords.push(coords[0]);
          allCoords.push(...coords);
        }
        (this.map.getSource('discovery-zones') as mapboxgl.GeoJSONSource).setData(
          {
            type: 'FeatureCollection',
            features: this.config().allDiscoveries.map(data => zoneToFeaturePolygon(data.graph))
          }
        );

        this.pathwaysFA = this.fb.array(this.config().pathways.map((pathway) => this.createPathwayFormGroup(pathway)));
        this.commentsFA = this.fb.array(this.config().comments.map((comment) => this.createCommentFormGroup(comment)));

        for (const pathway of this.pathwaysFA.controls) {
          this.addPathwayMapLayers(pathway as FormGroup);
          this.map.once('styledata', () => {
            const pE = this.initPathway(pathway as FormGroup, false);
            allCoords.push(...(pE.getData().vertices || []).map((v) => v.coords));
            pE.externalRedraw();
            this.pathwaysStrokeColorsUpdates(pathway as FormGroup);
          });
        }

        for (const comment of this.commentsFA.controls) {
          this.initComment(comment as FormGroup);
          if (comment.value?.coords) {
            allCoords.push(comment.value.coords);
          }
        }

        if (allCoords.length >= 1) {
          adjustMapBBox(allCoords, this.map, 0, 0, 120);
        } else {
          const lastLocation = this.getLastLocation();

          centerAndZoom = {
            zoom: 13,
            center: [lastLocation.lng, lastLocation.lat]
          };
        }
      }, 500);
    });

    this.map.on('click', (ev: any) => {
      const elevation = this.map.queryTerrainElevation(ev.lngLat, {exaggerated: false});

      if (this.currentPathway) {
        this.pathwaysEditEngines.get(this.currentPathway.value.uuid).clickOnMap(ev.lngLat);
        return;
      }

      if (this.currentComment) {
        this.commentsEditEngines.get(this.currentComment.value.uuid).clickOnMap(ev.lngLat);
        return;
      }

      const coords = {
        lng: parseFloat(ev.lngLat.lng.toFixed(6)),
        lat: parseFloat(ev.lngLat.lat.toFixed(6)),
        elev: elevation
      } as GeoCoords

      if (this.currentEditEngine) {
        this.storeLastLocation(coords);
        this.currentEditEngine.clickOnMap(coords);
      }

      this.hazardZonesCluster.instances.forEach((value) => {
        if (!value.editing) {
          value.nonEditClickOnMap(coords);
        }
      });
    });
  }

  override toggleMagStyle() {
    super.toggleMagStyle()

    this.map.once('styledata', () => {
      setTimeout(() => {
        this.hazardZonesCluster.instances.forEach((hz) => {
          hz.externalRedraw();
        });
      }, 100);
    });
  }

  apply(): MapConfigDataV2 {
    return {
      pathways: this.pathwaysFA.value,
      comments: this.commentsFA.value,
    };
  }

  isValid(): boolean {
    for (const inst of this.hazardZonesCluster.instances) {
      if (!inst.isValid()) {
        return false;
      }
    }

    return this.pathwaysFA?.valid && this.commentsFA?.valid;
  }

  getPathways(): FormArray {
    return this.pathwaysFA;
  }

  getPathwaysAsFormGroups(): FormGroup[] {
    return this.getPathways().controls as FormGroup[];
  }

  getComments(): FormArray {
    return this.commentsFA;
  }

  getCommentsAsFormGroups(): FormGroup[] {
    return this.getComments().controls as FormGroup[];
  }

  addNewHazardZone(zone?: QGraph): HazardZoneEditEngine {
    const newHazarZone = new HazardZoneEditEngine(this.map, this.mapControlPanelActions$.asObservable(), this.hazardZonesCluster, zone);

    this.hazardZonesCluster.instances.push(newHazarZone);

    //A little memory leak, because the subscription will linger after the zone was removed
    newHazarZone
      .mapControlPanelRequest$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.mapControlVisibility$.next(value);
      });

    newHazarZone
      .stopEdit$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.currentEditEngine = undefined;
        this.editMode = null;
      });

    newHazarZone
      .removeSelf$()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        const index = this.hazardZonesCluster.instances.indexOf(newHazarZone);
        this.removeHazardZone(index);
      });

    return newHazarZone;
  }

  removeHazardZone(index: number): void {
    this.hazardZonesCluster.instances.splice(index, 1)[0].clear();
    this.stopCurrentEdit();
  }

  clickHazardZoneBtn(zoneEngine: HazardZoneEditEngine): void {
    const sameZone = this.currentEditEngine === zoneEngine;
    this.stopCurrentEdit();

    if (sameZone) {
      return;
    }

    this.currentEditEngine = zoneEngine;
    this.currentEditEngine.startEditing();
    this.editMode = 'hazardZone';
  }

  stopCurrentEdit(): void {
    if (this.currentEditEngine) {
      this.currentEditEngine.stopEditing();
      this.currentEditEngine = undefined;
    }

    this.editMode = null;
  }

  isEditingAnything(): boolean {
    if (this.hazardZonesCluster?.instances) {
      for (const hz of this.hazardZonesCluster.instances) {
        if (hz.editing) {
          return true;
        }
      }
    }

    return Array.from(this.pathwaysEditEngines.values()).some(e => e.editing)
    || Array.from(this.commentsEditEngines.values()).some(e => e.editing);
  }

  ngOnDestroy(): void {
    this.mapControlPanelActions$.complete();
    this.mapControlVisibility$.complete();
    this.destroy$.next();
    this.destroy$.complete();

    super.destroy();
    this.hazardZonesCluster.instances.forEach((value) => {
      value.destroy();
    });
  }

  metersToPretty = metersToPretty;
}
